/**
 * Shared audio types.
 *
 * The renderer reads a single `AudioFeatures` snapshot per frame.
 * Anything that produces features (live analyser, pre-analyzed timeline)
 * implements `FeatureProvider`.
 */

export interface AudioFeatures {
  /** Bass band energy, 0..1 (adaptive-normalized, 20–250 Hz). */
  bass: number;
  /** Mid band energy, 0..1 (250–4000 Hz). */
  mid: number;
  /** Treble band energy, 0..1 (4000–16000 Hz). */
  treble: number;
  /** Combined level 0..1 — convenience for global "loudness". */
  level: number;
  /** Estimated BPM (running average). 0 until enough beats observed. */
  bpm: number;
  /**
   * Beat envelope 0..1. Spikes to 1 on a detected beat and decays
   * smoothly so the renderer can read a stable "punch" signal each frame
   * without missing a beat between renders.
   */
  beat: number;
  /** Number of beats fired since session start. */
  beatCount: number;
  /** Wall-clock time of the snapshot (ms, performance.now). */
  t: number;
  /**
   * Optional normalized FFT magnitude array (~64-128 bins, 0..1 each).
   * The Audio Tide scene reads this to render the spectrum as 3D terrain.
   * Providers that don't have a real spectrum (e.g. preanalyzed timelines)
   * may leave this undefined; consumers must handle that case.
   */
  spectrum?: Float32Array;
  /**
   * Optional time-domain PCM waveform (-1..+1, ~512 samples).
   * The waveform overlay layer reads this each frame to draw a glowing
   * line of the actual audio waveform on top of the scene.
   */
  waveform?: Float32Array;
}

/** Empty / silent features. */
export const ZERO_FEATURES: AudioFeatures = {
  bass: 0,
  mid: 0,
  treble: 0,
  level: 0,
  bpm: 0,
  beat: 0,
  beatCount: 0,
  t: 0,
};

export interface FeatureProvider {
  /** Most recent features, safe to call every frame. */
  read(): AudioFeatures;
  /** Free underlying audio nodes / streams / timers. */
  stop(): void;
}

export type SourceKind = "mic" | "tab" | "file" | "preanalyzed" | "none";

export interface SourceInfo {
  kind: SourceKind;
  label: string;
  /** Optional `<audio>` element to expose for playback controls (file mode). */
  mediaElement?: HTMLAudioElement;
  /** Optional MediaStream for recording (mic / tab modes). */
  stream?: MediaStream;
  /**
   * Optional destination node whose stream can be used for recording when
   * the source itself isn't a MediaStream (file / preanalyzed modes).
   */
  recordStream?: MediaStream;
}
